import classNames from 'classnames';
import * as _ from 'lodash-es';
import type { AttributeMap, Op } from 'quill/core';
import { Delta } from 'quill/core';
import type { ReactElement } from 'react';
import React, { cloneElement } from 'react';
import * as styles from './DeltaPreviewParagraph.module.scss';
import { BLOT_VALUE_ATTRIBUTE } from '../editor/constants';
import type { AttachmentFile } from '../editor/formats/Attachment';
import { BaseAttachmentMention } from '../editor/formats/AttachmentMention';
import EDITOR_INTEGRATION_MENTIONS from '../editor/mentions/EditorIntegrationMentions';
import ExternalPostMention from '../editor/mentions/ExternalPostMention';
import GroupMention from '../editor/mentions/GroupMention';
import IntegrationMention from '../editor/mentions/IntegrationMention';
import PostMention from '../editor/mentions/PostMention';
import TopicMention from '../editor/mentions/TopicMention';
import UserMention from '../editor/mentions/UserMention';
import type { IntegrationMentionType, Mention } from '../editor/types';
import { isAttachmentUploaded } from '../utils/AttachmentUploader';
import Link from '../utils/router/Link';

interface GetMentionNodeOptions {
  link: boolean;
}

const getMentionNode = (
  type: IntegrationMentionType,
  id: string,
  options: GetMentionNodeOptions,
  {
    attributes,
    params,
  }: {
    attributes: AttributeMap;
    params?: Mention['params'];
  },
) => {
  const commonProps = {
    id,
    content: attributes.content as string,
    mentionText: attributes['mention-text'] as string,
    ...options,
  };

  switch (type) {
    case 'external-post':
      return <ExternalPostMention {...commonProps} />;
    case 'post':
      return <PostMention {...commonProps} />;
    case 'topic':
      return <TopicMention {...commonProps} />;
    case 'user':
      return <UserMention {...commonProps} />;
    case 'group':
      return <GroupMention {...commonProps} />;
    default:
      return EDITOR_INTEGRATION_MENTIONS[type] ? (
        <IntegrationMention type={type} {...commonProps} params={params} />
      ) : null;
  }
};

const isMergeable = (previous: ReactElement, element: ReactElement) =>
  previous.type === element.type &&
  _.isEqual(
    _.omit(previous.props, ['children']),
    _.omit(element.props, ['children']),
  );

const mapElementWithClassName = (
  element: ReactElement | string,
  className: string,
) => {
  return typeof element === 'string' ? (
    <span className={className}>{element}</span>
  ) : (
    <element.type
      className={classNames(element.props.className, className)}
      {...element.props}
    />
  );
};

const formatMapper: {
  name: string;
  map: (
    element: ReactElement | string,
    value: unknown,
  ) => ReactElement | string;
}[] = [
  {
    name: 'link',
    map: (element, value) =>
      typeof value === 'string' ? (
        <Link className="ql-link" to={value} newTab="ugc">
          {element}
        </Link>
      ) : (
        element
      ),
  },
  { name: 'strike', map: (element) => <s>{element}</s> },
  { name: 'italic', map: (element) => <em>{element}</em> },
  { name: 'bold', map: (element) => <strong>{element}</strong> },
  { name: 'code', map: (element) => <code>{element}</code> },
  {
    name: 'highlight',
    map: (element, value) =>
      mapElementWithClassName(element, `highlight-${String(value)}`),
  },
  {
    name: 'diff',
    map: (element, value) =>
      mapElementWithClassName(element, `ql-diff-${String(value)}`),
  },
];

interface DeltaPreviewBlockProps {
  content: Op[] | Delta;
  link: boolean;
}
const DeltaPreviewParagraph = ({ content, link }: DeltaPreviewBlockProps) => {
  const { ops } = new Delta(content);
  if (!ops.length) return <br />;

  return (
    <>
      {ops.reduce((block, { insert, attributes = {} }, index) => {
        if (!insert) return block;

        if (typeof insert === 'object') {
          const key = _.first(Object.keys(insert)) as string;
          const value = insert[key];

          if (key === 'attachment-mention') {
            const attachmentFiles = value as AttachmentFile[];
            const mentionText =
              typeof attributes['mention-text'] === 'string'
                ? attributes['mention-text']
                : undefined;

            return block.concat(
              <BaseAttachmentMention
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                mentionText={mentionText}
                visibleAttachments={attachmentFiles.filter(
                  isAttachmentUploaded,
                )}
              />,
            );
          }

          if (key === 'mention') {
            const { type, id, params } = (
              typeof value === 'object' && value ? value : {}
            ) as Partial<Mention>;

            const mentionNode =
              type &&
              id &&
              getMentionNode(
                type,
                id,
                { link },
                {
                  attributes,
                  params,
                },
              );

            if (mentionNode) {
              return block.concat(
                <span
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className="ql-mention"
                  {...{
                    [BLOT_VALUE_ATTRIBUTE]: JSON.stringify({ id, type }),
                  }}
                >
                  {mentionNode}
                </span>,
              );
            }
          }

          return block;
        }

        const formattedInsert = formatMapper
          .filter(({ name }) => attributes[name] && (link || name !== 'link'))
          .reduce<
            string | ReactElement
          >((element, { name, map }) => map(element, attributes[name]), insert);

        const element =
          typeof formattedInsert === 'string' ? (
            <>{formattedInsert}</>
          ) : (
            formattedInsert
          );

        if (attributes.searchHighlight) {
          return block.concat(
            <em
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className={styles.searchHighlight}
            >
              {element}
            </em>,
          );
        }

        const lastElement = _.last(block);
        const shouldMerge = lastElement && isMergeable(lastElement, element);
        return shouldMerge
          ? [
              ...block.slice(0, -1),
              cloneElement(lastElement, {
                children: (
                  <>
                    {lastElement.props.children}
                    {element.props.children}
                  </>
                ),
              }),
            ]
          : // eslint-disable-next-line react/no-array-index-key
            block.concat(<element.type {...element.props} key={index} />);
      }, [] as ReactElement[])}
    </>
  );
};

export default DeltaPreviewParagraph;
